import * as React from "react";
import {
  GridColDef,
  GridValueFormatterParams,
  GridSlotsComponentsProps,
} from "@mui/x-data-grid";
import TableFilters from "./TableFilters";
import { LoanType } from "../../types/loanType";
import { currencyFormatter } from "../../utils/utils";
import "./styles.scss";
import { useEffect, useState } from "react";
import { currentYear, currentMonth } from "../../variables/constants";
import { getLoanSummary } from "../../services/reportsService";
import TableRowsLoader from "../common/Loader/tableLoader";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

declare module "@mui/x-data-grid" {
  interface FooterPropsOverrides {
    total: {
      initialBalance: number;
      debits: number;
      credits: number;
      interest: number;
      balance: number;
    };
  }
}

export function CustomFooterStatusComponent(
  props: NonNullable<GridSlotsComponentsProps["footer"]>
) {
  const { total } = props;
  return (
    <div className="footer-status">
      <div className="footer-total">Total</div>
      <div className="footer-text">
        {currencyFormatter.format(total?.initialBalance ?? 0)}
      </div>
      <div className="footer-text">
        {currencyFormatter.format(total?.debits ?? 0)}
      </div>
      <div className="footer-text">
        {currencyFormatter.format(total?.credits ?? 0)}
      </div>
      <div className="footer-text text-end">
        {currencyFormatter.format(total?.interest ?? 0)}
      </div>
      <div className="footer-text text-end">
        {currencyFormatter.format(total?.balance ?? 0)}
      </div>
    </div>
  );
}

const LoanSummaryTable = () => {
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [summaryData, setSummaryData] = useState<LoanType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getAllTransactions = () => {
    setIsLoading(true);
    getLoanSummary(selectedMonth, selectedYear)
      .then((response) => {
        let sortedData = response.sort((a, b) => {
          //Then, sort by investor
          const nameA = a.investor;
          const nameB = b.investor;
          return nameA.localeCompare(nameB);
        });
        setSummaryData(sortedData);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getAllTransactions();
  }, []);

  useEffect(() => {
    getAllTransactions();
  }, [selectedMonth, selectedYear]);

  const valueFormatter = (params: GridValueFormatterParams) => {
    const numericValue = parseFloat(params.value as string); // Attempt to parse value as a number
    if (!isNaN(numericValue)) {
      // Check if parsed value is a valid number
      return currencyFormatter.format(numericValue); // Format as currency
    } else {
      return ""; // Return empty string or handle non-numeric values as needed
    }
  };

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "investor",
        headerName: "Client",
        minWidth: 200,
        sortable: true,
        headerClassName: "header-datagrid",
      },

      {
        field: "account",
        headerName: "Account",
        width: 200,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "startingBalance",
        headerName: "Initial Balance",
        minWidth: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter,
      },
      {
        field: "debits",
        headerName: "Debits",
        width: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter,
      },
      {
        field: "credits",
        headerName: "Credits",
        width: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter,
      },
      {
        field: "interest",
        headerName: "Interest",
        type: "number",
        minWidth: 150,
        headerClassName: "header-datagrid",
        valueFormatter,
      },
      {
        field: "endingBalance",
        headerName: "Balance",
        type: "number",
        minWidth: 150,
        headerClassName: "header-datagrid",
        valueFormatter,
      },
    ],
    []
  );
  const total = React.useMemo(() => {
    const total = summaryData.reduce(
      (acc, item) => {
        acc.initialBalance += item.startingBalance;
        acc.debits += item.debits;
        acc.credits += item.credits;
        acc.interest += item.interest;
        acc.balance += item.endingBalance;
        return acc;
      },
      {
        initialBalance: 0,
        debits: 0,
        credits: 0,
        interest: 0,
        balance: 0,
      }
    );
    return total;
  }, [summaryData]);

  return (
    <div className="data-table">
      <TableFilters
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />
      {isLoading ? (
        <TableRowsLoader rowsNum={10} />
      ) : summaryData.length > 0 ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ marginTop: "15px", paddingLeft: "15px" }}
        >
          <Table className="partners-table-container" aria-label="simple table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.field} className="client-header">
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row" className="table-cell">
                    {row.investor}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {row?.account}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.startingBalance)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.debits)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.credits)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.interest)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    {currencyFormatter.format(row.endingBalance)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell component="th" scope="row" className="client-header">
                  {"Total"}
                </TableCell>
                <TableCell align="left" className="client-header"></TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.initialBalance)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.debits)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.credits)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.interest)}
                </TableCell>
                <TableCell align="left" className="client-header">
                  {currencyFormatter.format(total.balance)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography className="no-results">
          No Loan Summary records found
        </Typography>
      )}
    </div>
  );
};

export default LoanSummaryTable;
